import { RequirePayoutsAccountingSettingsUser } from '@/components/auth/RequirePayoutsAccountingSettingsUser';
import {
  useGetOrganizationPausedSettingsQuery,
  usePutOrganizationPausedSettingsMutation,
  PutOrganizationPausedSettings,
} from '@/services/organizations';
import { OrganizationNode } from '@/types/Organizations';
import { Table, useOpenClose } from '@palmetto/palmetto-components';
import { ReactNode, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { OrganizationSettingsTypes, ProgramType } from 'types';

import { OrganizationSettingsCard } from '../shared/OrganizationSettingsCard';
import { PausedSettingsFormValues, PausedSettingsEditModal } from '../shared/PausedSettingsEditModal';

const columnPausedSettings = [
  {
    heading: 'Name',
    dataKey: 'name',
    width: 200,
  },
  {
    heading: 'Value',
    dataKey: 'value',
    width: 100,
    align: 'center' as const,
  },
];

const renderEditButton = (button: ReactNode) => (
  <RequirePayoutsAccountingSettingsUser>{button}</RequirePayoutsAccountingSettingsUser>
);

interface SafeHarborSettingsProps {
  organization: OrganizationNode;
  programType: ProgramType;
}

export const SafeHarborSettings = ({ organization, programType }: SafeHarborSettingsProps) => {
  const { alias } = useParams<{ alias: any }>();

  const {
    data: safeHarborPausedSettings,
    isFetching: isLoadingSafeHarborPausedSettings,
    error: safeHarborPausedSettingsError,
  } = useGetOrganizationPausedSettingsQuery(
    {
      alias,
      programType,
      settingsType: OrganizationSettingsTypes.safeHarborPausedSettings,
    },
    {
      skip: !alias,
    },
  );
  const [setSafeHarborPausedSettings] = usePutOrganizationPausedSettingsMutation();
  const {
    handleClose: handleCloseSafeHarborPausedSettings,
    handleOpen: handleOpenSafeHarborPausedSettings,
    isOpen: isOpenSafeHarborPausedSettings,
  } = useOpenClose();

  const handleSubmitSafeHarborPausedSettings = useCallback(
    async (values: PausedSettingsFormValues) => {
      const payload = {
        alias,
        programType,
        settingsType: OrganizationSettingsTypes.safeHarborPausedSettings,
        value: {
          inherit: values.inherit,
          ...(!values.inherit && { pausedDays: values.pausedDays }),
        },
      } satisfies PutOrganizationPausedSettings;
      await setSafeHarborPausedSettings(payload).unwrap();
    },
    [alias, programType, setSafeHarborPausedSettings],
  );

  const safeHarborPausedSettingsRows = useMemo(() => {
    const pausedDays = safeHarborPausedSettings?.value?.pausedDays;
    if (pausedDays === undefined) return [];

    return [
      {
        id: 'pausedDays',
        name: 'Pause Days',
        value: pausedDays,
      },
    ];
  }, [safeHarborPausedSettings]);

  return (
    <>
      <PausedSettingsEditModal
        isOpen={isOpenSafeHarborPausedSettings}
        handleClose={handleCloseSafeHarborPausedSettings}
        handleSubmit={handleSubmitSafeHarborPausedSettings}
        currentSettings={safeHarborPausedSettings?.value}
        modalId="edit-safe-harbor-paused-settings"
        modalTitle="Edit Safe Harbor Pause Days"
      />
      <OrganizationSettingsCard
        cardTitle="Safe Harbor Pause Days"
        organization={organization}
        isLoading={isLoadingSafeHarborPausedSettings}
        error={safeHarborPausedSettingsError}
        isInherited={!safeHarborPausedSettingsError && safeHarborPausedSettings?.value?.inherit}
        onClick={handleOpenSafeHarborPausedSettings}
        cardPadding="0 md xl"
        renderEditButton={renderEditButton}
      >
        <Table
          rowKey="id"
          columns={columnPausedSettings}
          rows={safeHarborPausedSettingsRows}
          isLoading={isLoadingSafeHarborPausedSettings}
        />
      </OrganizationSettingsCard>
    </>
  );
};
